// src/pages/training-videos-library/index.jsx
// "Ajouter une vidéo" and "Gérer catégories" are admin-only.
// useAdminRole() controls visibility of both controls.

import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import VideoCard from './components/VideoCard';
import CategoryFilter from './components/CategoryFilter';
import SearchBar from './components/SearchBar';
import PlaylistSection from './components/PlaylistSection';
import VideoPlayer from './components/VideoPlayer';
import StatsOverview from './components/StatsOverview';
import AddVideoModal from './components/AddVideoModal';
import CategoryManagerModal from './components/CategoryManagerModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

import {
  useVideos,
  useCategoriesWithCount,
  usePlaylists,
  useStats,
  useVideoActions,
} from '../../hooks/useTrainingLibrary';
import { useAdminRole } from '../../hooks/useAdminRole';

const TrainingVideosLibrary = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { isAdmin }                    = useAdminRole();   // ← role check

  // ── UI state ──────────────────────────────────────────────────
  const [selectedCategory, setSelectedCategory]   = useState('all');
  const [searchQuery, setSearchQuery]             = useState('');
  const [sortBy, setSortBy]                       = useState('recent');
  const [viewMode, setViewMode]                   = useState('grid');
  const [selectedVideo, setSelectedVideo]         = useState(null);
  const [showAddModal, setShowAddModal]           = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // ── Data ──────────────────────────────────────────────────────
  const { categories, refetch: refetchCategories } = useCategoriesWithCount();
  const { videos, loading, refetch: refetchVideos } = useVideos({
    categorySlug: selectedCategory,
    searchQuery,
    sortBy,
  });
  const { playlists }  = usePlaylists();
  const stats          = useStats();
  const { incrementView, saveProgress, toggleBookmark } = useVideoActions();

  const bookmarkedVideos = videos.filter((v) => v.isBookmarked).slice(0, 5);

  // ── Handlers ──────────────────────────────────────────────────
  const handleVideoPlay = (video) => {
    setSelectedVideo(video);
    incrementView(video.id);
  };

  const handleVideoComplete = async (videoId) => {
    await saveProgress(videoId, 100, true);
    refetchVideos();
  };

  const handleVideoProgress = async (videoId, pct, isCompleted) => {
    await saveProgress(videoId, pct, isCompleted);
  };

  const handleBookmark = async (videoId) => {
    const video = videos.find((v) => v.id === videoId);
    if (!video) return;
    await toggleBookmark(videoId, video.isBookmarked);
    refetchVideos();
  };

  const handleVideoAdded = () => refetchVideos();

  const handleCategoriesChanged = () => {
    refetchCategories();
    refetchVideos();
  };

  const sortOptions = [
    { value: 'recent',   label: 'Plus récents'    },
    { value: 'popular',  label: 'Plus populaires' },
    { value: 'title',    label: 'Titre (A-Z)'     },
    { value: 'duration', label: 'Durée'           },
  ];

  const breadcrumbItems = [
    { label: 'Tableau de bord',           path: '/dashboard',               icon: 'Home'          },
    { label: 'Bibliothèque de formation', path: '/training-videos-library', icon: 'GraduationCap' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="p-6 space-y-6">

          {/* ── Header ── */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-foreground flex items-center gap-3">
                <Icon name="GraduationCap" size={32} color="var(--color-primary)" />
                Bibliothèque de formation
              </h1>
              <p className="text-muted-foreground mt-2">
                Accédez à des ressources éducatives complètes pour améliorer vos compétences
              </p>
            </div>

            {/* Admin-only actions */}
            {isAdmin && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="LayoutGrid"
                  iconPosition="left"
                  onClick={() => setShowCategoryModal(true)}
                >
                  Catégories
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowAddModal(true)}
                >
                  Ajouter une vidéo
                </Button>
              </div>
            )}
          </div>

          {/* ── Stats ── */}
          <StatsOverview stats={stats} />

          {/* ── Grid ── */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

            <div className="lg:col-span-3 space-y-6">
              <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                <SearchBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  resultsCount={videos.length}
                />

                {/* Pass isAdmin + handler to CategoryFilter */}
                <CategoryFilter
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  isAdmin={isAdmin}
                  onManageClick={() => setShowCategoryModal(true)}
                />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {['grid', 'list'].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setViewMode(mode)}
                        className={`p-2 rounded-lg transition-colors duration-200 ${
                          viewMode === mode
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        <Icon name={mode === 'grid' ? 'Grid3x3' : 'List'} size={20} />
                      </button>
                    ))}
                  </div>
                  <Select options={sortOptions} value={sortBy} onChange={setSortBy} className="w-48" />
                </div>
              </div>

              {/* Skeleton */}
              {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-card border border-border rounded-lg overflow-hidden animate-pulse">
                      <div className="h-44 bg-muted" />
                      <div className="p-4 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-full" />
                        <div className="h-3 bg-muted rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Videos */}
              {!loading && (
                <div className={`grid gap-6 ${
                  viewMode === 'grid'
                    ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
                    : 'grid-cols-1'
                }`}>
                  {videos.map((video) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      onPlay={handleVideoPlay}
                      onBookmark={handleBookmark}
                    />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!loading && videos.length === 0 && (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Aucune vidéo trouvée</h3>
                  <p className="text-muted-foreground mb-4">
                    Essayez de modifier vos critères de recherche ou de filtrage
                  </p>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => setShowAddModal(true)}
                    >
                      Ajouter la première vidéo
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* ── Sidebar ── */}
            <div className="space-y-6">
              <PlaylistSection
                playlists={playlists}
                onPlaylistSelect={(pl) => console.log('playlist', pl.id)}
              />

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Bookmark" size={20} color="var(--color-primary)" />
                  Vidéos favorites
                </h3>
                {bookmarkedVideos.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune vidéo mise en favori pour l'instant.</p>
                ) : (
                  <div className="space-y-3">
                    {bookmarkedVideos.map((video) => (
                      <div
                        key={video.id}
                        onClick={() => handleVideoPlay(video)}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      >
                        <div className="w-16 h-12 rounded overflow-hidden flex-shrink-0 bg-muted">
                          <img src={video.thumbnail} alt={video.thumbnailAlt} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{video.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ── Modals ── */}
      {selectedVideo && (
        <VideoPlayer
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
          onComplete={handleVideoComplete}
          onProgress={handleVideoProgress}
        />
      )}

      {showAddModal && isAdmin && (
        <AddVideoModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleVideoAdded}
        />
      )}

      {showCategoryModal && isAdmin && (
        <CategoryManagerModal
          onClose={() => setShowCategoryModal(false)}
          onCategoriesChanged={handleCategoriesChanged}
        />
      )}
    </div>
  );
};

export default TrainingVideosLibrary;